import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Tabs,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Space,
  Typography,
  message,
  Avatar,
  Dropdown,
  Divider
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  SaveOutlined,
  SettingOutlined,
  DatabaseOutlined,
  MailOutlined,
  CloudOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

export default function SystemConfiguration() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generalForm] = Form.useForm();
  const [databaseForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [storageForm] = Form.useForm();

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      const [generalRes, dbRes, emailRes, storageRes] = await Promise.allSettled([
        api.get('/settings/general'),
        api.get('/settings/database'),
        api.get('/settings/email'),
        api.get('/settings/storage')
      ]);

      if (generalRes.status === 'fulfilled') {
        generalForm.setFieldsValue(generalRes.value.data);
      }
      if (dbRes.status === 'fulfilled') {
        databaseForm.setFieldsValue(dbRes.value.data);
      }
      if (emailRes.status === 'fulfilled') {
        emailForm.setFieldsValue(emailRes.value.data);
      }
      if (storageRes.status === 'fulfilled') {
        storageForm.setFieldsValue(storageRes.value.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/super-admin/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleSaveGeneral = async (values) => {
    setLoading(true);
    try {
      await api.put('/settings/general', values);
      message.success('General settings saved successfully');
    } catch (error) {
      message.error('Failed to save general settings');
      console.error('Error saving general settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDatabase = async (values) => {
    setLoading(true);
    try {
      await api.put('/settings/database', values);
      message.success('Database settings saved successfully');
    } catch (error) {
      message.error('Failed to save database settings');
      console.error('Error saving database settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async (values) => {
    setLoading(true);
    try {
      await api.put('/settings/email', values);
      message.success('Email settings saved successfully');
    } catch (error) {
      message.error('Failed to save email settings');
      console.error('Error saving email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStorage = async (values) => {
    setLoading(true);
    try {
      await api.put('/settings/storage', values);
      message.success('Storage settings saved successfully');
    } catch (error) {
      message.error('Failed to save storage settings');
      console.error('Error saving storage settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      key: '1',
      label: (
        <span>
          <SettingOutlined />
          General
        </span>
      ),
      children: (
        <Card>
          <Form
            form={generalForm}
            layout="vertical"
            onFinish={handleSaveGeneral}
            initialValues={{
              platform_name: 'USSEDU Platform',
              max_concurrent_sessions: 100,
              session_timeout: 30,
              enable_notifications: true,
              enable_analytics: true,
            }}
          >
            <Form.Item
              name="platform_name"
              label="Platform Name"
              rules={[{ required: true, message: 'Please enter platform name' }]}
            >
              <Input placeholder="USSEDU Platform" />
            </Form.Item>

            <Form.Item
              name="max_concurrent_sessions"
              label="Maximum Concurrent Sessions"
              rules={[{ required: true, message: 'Please enter max sessions' }]}
            >
              <InputNumber min={1} max={1000} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="session_timeout"
              label="Session Timeout (minutes)"
              rules={[{ required: true, message: 'Please enter timeout' }]}
            >
              <InputNumber min={5} max={120} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="enable_notifications" label="Enable Notifications" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item name="enable_analytics" label="Enable Analytics" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Divider />

            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: '2',
      label: (
        <span>
          <DatabaseOutlined />
          Database
        </span>
      ),
      children: (
        <Card>
          <Form
            form={databaseForm}
            layout="vertical"
            onFinish={handleSaveDatabase}
            initialValues={{
              db_host: 'dpgsql01.infra.uss',
              db_port: 5432,
              db_name: 'portal_ussedu_net',
              db_max_connections: 100,
            }}
          >
            <Form.Item
              name="db_host"
              label="Database Host"
              rules={[{ required: true, message: 'Please enter database host' }]}
            >
              <Input placeholder="dpgsql01.infra.uss" />
            </Form.Item>

            <Form.Item
              name="db_port"
              label="Database Port"
              rules={[{ required: true, message: 'Please enter database port' }]}
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="db_name"
              label="Database Name"
              rules={[{ required: true, message: 'Please enter database name' }]}
            >
              <Input placeholder="portal_ussedu_net" />
            </Form.Item>

            <Form.Item
              name="db_max_connections"
              label="Max Connections"
              rules={[{ required: true, message: 'Please enter max connections' }]}
            >
              <InputNumber min={10} max={500} style={{ width: '100%' }} />
            </Form.Item>

            <Divider />

            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: '3',
      label: (
        <span>
          <MailOutlined />
          Email
        </span>
      ),
      children: (
        <Card>
          <Form
            form={emailForm}
            layout="vertical"
            onFinish={handleSaveEmail}
            initialValues={{
              smtp_host: 'smtp.example.com',
              smtp_port: 587,
              smtp_from_email: 'noreply@ussedu.net',
              smtp_from_name: 'USSEDU Platform',
            }}
          >
            <Form.Item
              name="smtp_host"
              label="SMTP Host"
              rules={[{ required: true, message: 'Please enter SMTP host' }]}
            >
              <Input placeholder="smtp.example.com" />
            </Form.Item>

            <Form.Item
              name="smtp_port"
              label="SMTP Port"
              rules={[{ required: true, message: 'Please enter SMTP port' }]}
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="smtp_from_email"
              label="From Email"
              rules={[
                { required: true, message: 'Please enter from email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="noreply@ussedu.net" />
            </Form.Item>

            <Form.Item
              name="smtp_from_name"
              label="From Name"
              rules={[{ required: true, message: 'Please enter from name' }]}
            >
              <Input placeholder="USSEDU Platform" />
            </Form.Item>

            <Form.Item
              name="smtp_username"
              label="SMTP Username"
              rules={[{ required: true, message: 'Please enter SMTP username' }]}
            >
              <Input placeholder="smtp_user@example.com" />
            </Form.Item>

            <Form.Item
              name="smtp_password"
              label="SMTP Password"
              rules={[{ required: true, message: 'Please enter SMTP password' }]}
            >
              <Input.Password placeholder="Enter SMTP password" />
            </Form.Item>

            <Form.Item name="smtp_tls_enabled" label="Enable TLS/SSL" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Divider />

            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: '4',
      label: (
        <span>
          <CloudOutlined />
          Storage
        </span>
      ),
      children: (
        <Card>
          <Form
            form={storageForm}
            layout="vertical"
            onFinish={handleSaveStorage}
            initialValues={{
              s3_endpoint: 'https://minio.example.com',
              s3_region: 'us-east-1',
              s3_bucket_materials: 'ussedu-training-materials',
              s3_bucket_labs: 'ussedu-lab-materials',
              max_file_size_mb: 2048,
            }}
          >
            <Form.Item
              name="s3_endpoint"
              label="MinIO S3 Endpoint"
              rules={[{ required: true, message: 'Please enter MinIO S3 endpoint' }]}
            >
              <Input placeholder="https://minio.example.com" />
            </Form.Item>

            <Form.Item
              name="s3_region"
              label="S3 Region"
              rules={[{ required: true, message: 'Please enter S3 region' }]}
            >
              <Input placeholder="us-east-1" />
            </Form.Item>

            <Form.Item
              name="s3_access_key"
              label="Storage Access Key"
              rules={[{ required: true, message: 'Please enter storage access key' }]}
            >
              <Input placeholder="Enter MinIO access key" />
            </Form.Item>

            <Form.Item
              name="s3_secret_key"
              label="Storage Secret Key"
              rules={[{ required: true, message: 'Please enter storage secret key' }]}
            >
              <Input.Password placeholder="Enter MinIO secret key" />
            </Form.Item>

            <Form.Item
              name="s3_bucket_materials"
              label="Materials Bucket"
              rules={[{ required: true, message: 'Please enter materials bucket' }]}
            >
              <Input placeholder="ussedu-training-materials" />
            </Form.Item>

            <Form.Item
              name="s3_bucket_labs"
              label="Labs Bucket"
              rules={[{ required: true, message: 'Please enter labs bucket' }]}
            >
              <Input placeholder="ussedu-lab-materials" />
            </Form.Item>

            <Form.Item name="s3_tls_enabled" label="Enable TLS/SSL" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item
              name="max_file_size_mb"
              label="Max File Size (MB)"
              rules={[{ required: true, message: 'Please enter max file size' }]}
            >
              <InputNumber min={1} max={10240} style={{ width: '100%' }} />
            </Form.Item>

            <Divider />

            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/super-admin/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">System Configuration</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>System Configuration</Title>
          <Text type="secondary">Manage platform settings and configuration</Text>
        </div>

        <Tabs items={tabs} defaultActiveKey="1" />
      </Content>
    </Layout>
  );
}
